import { map, mapValues } from 'lodash-es'
import type {
  PlanTableTemplateOptions,
  PositionListElementTypeVisibility,
  PositionListTemplateOptions,
  ShearAnchorsTemplateOptions,
  TensileAnchorsTemplateOptions,
} from './types'

export const POSITION_LIST_PLAN_TABLE_TEMPLATE_KEY = 'position-list-plan-table-template'

export const ASSEMBLIES_PLAN_TABLE_TEMPLATE_KEY = 'assemblies-plan-table-template'

export const TENSILE_ANCHORS_PLAN_TABLE_TEMPLATE_KEY = 'tensile-anchors-plan-table-template'

export const SHEAR_ANCHORS_PLAN_TABLE_TEMPLATE_KEY = 'shear-anchors-plan-table-template'

export const ONLY_SHOW_ELEMENTS_WITH_COMMENT_OPTION_KEY = 'onlyShowElementsWithComment'

export const DEFAULT_ONLY_SHOW_ELEMENTS_WITH_COMMENT = true

// Typed as a full record so a new element type can't be missing a label or a default.
const POSITION_LIST_ELEMENT_TYPE_LABELS: Record<keyof PositionListElementTypeVisibility, string> = {
  columns: 'Stützen',
  beams: 'Unterzüge',
  purlins: 'Pfetten',
  lintels: 'Stürze',
  rips: 'Rippen',
  verticalSlabs: 'Deckenpositionen',
  verticalRoofSlabs: 'Dachpositionen',
}

export const POSITION_LIST_ELEMENT_TYPE_OPTIONS = map(
  POSITION_LIST_ELEMENT_TYPE_LABELS,
  (label, value) => ({
    value: value as keyof PositionListElementTypeVisibility,
    label,
  }),
)

export const defaultPositionListElementTypeVisibility = (): PositionListElementTypeVisibility =>
  mapValues(POSITION_LIST_ELEMENT_TYPE_LABELS, () => true)

export const defaultPositionListTemplateOptions = (): PositionListTemplateOptions => ({
  [ONLY_SHOW_ELEMENTS_WITH_COMMENT_OPTION_KEY]: DEFAULT_ONLY_SHOW_ELEMENTS_WITH_COMMENT,
  elementTypes: defaultPositionListElementTypeVisibility(),
})

export const isOnlyShowElementsWithCommentEnabled = (
  templateOptions: PlanTableTemplateOptions | undefined,
): boolean => {
  const value = templateOptions?.[ONLY_SHOW_ELEMENTS_WITH_COMMENT_OPTION_KEY]
  return typeof value === 'boolean' ? value : false
}

export const getPositionListElementTypeVisibility = (
  templateOptions: PlanTableTemplateOptions | undefined,
): PositionListElementTypeVisibility => ({
  ...defaultPositionListElementTypeVisibility(),
  ...templateOptions?.elementTypes,
})

export const togglePositionListElementType = (
  templateOptions: PlanTableTemplateOptions | undefined,
  elementType: keyof PositionListElementTypeVisibility,
): PlanTableTemplateOptions => {
  const elementTypes = getPositionListElementTypeVisibility(templateOptions)
  return {
    ...templateOptions,
    elementTypes: { ...elementTypes, [elementType]: !elementTypes[elementType] },
  }
}

export const defaultTensileAnchorsTemplateOptions = (): TensileAnchorsTemplateOptions => ({
  showTotalAnchorCount: false,
  showStoreyAnchorCount: true,
})

export const getTensileAnchorsTemplateOptions = (
  templateOptions: PlanTableTemplateOptions | undefined,
): TensileAnchorsTemplateOptions => {
  const defaults = defaultTensileAnchorsTemplateOptions()
  const { showTotalAnchorCount, showStoreyAnchorCount } = templateOptions ?? {}
  return {
    showTotalAnchorCount:
      typeof showTotalAnchorCount === 'boolean'
        ? showTotalAnchorCount
        : defaults.showTotalAnchorCount,
    showStoreyAnchorCount:
      typeof showStoreyAnchorCount === 'boolean'
        ? showStoreyAnchorCount
        : defaults.showStoreyAnchorCount,
  }
}

export const defaultShearAnchorsTemplateOptions = (): ShearAnchorsTemplateOptions => ({
  showStoreyAnchorCount: true,
})

export const getShearAnchorsTemplateOptions = (
  templateOptions: PlanTableTemplateOptions | undefined,
): ShearAnchorsTemplateOptions => {
  const { showStoreyAnchorCount } = templateOptions ?? {}
  return {
    showStoreyAnchorCount:
      typeof showStoreyAnchorCount === 'boolean'
        ? showStoreyAnchorCount
        : defaultShearAnchorsTemplateOptions().showStoreyAnchorCount,
  }
}
